import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { getAdminId } from '../utils/getAdminId';
import { Invoice, InvoiceItem, SalesReportFilter, SalesReportData } from '../types/invoice';

export const useInvoices = () => {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      loadInvoices();
    }
  }, [user]);

  const loadInvoices = async () => {
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      // Super admin sees all invoices; everyone else is scoped to their tenant.
      const adminId = user.role === 'super_admin' ? null : getAdminId(user);

      let query = supabase
        .from('invoices')
        .select(`
          *,
          invoice_items (
            id,
            invoice_id,
            product_id,
            description,
            quantity,
            unit_price,
            total_price,
            sku,
            color_name
          )
        `)
        .order('created_at', { ascending: false });

      if (adminId) {
        query = query.eq('admin_id', adminId);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;

      // Derive Color and SKU live from the linked product so they show on every
      // invoice type (POS, manual, B2B), falling back to the stored line values
      // when a line has no product_id or the product/color lookup misses.
      const productIds = Array.from(
        new Set(
          (data || []).flatMap((inv: any) =>
            (inv.invoice_items || [])
              .map((item: any) => item.product_id)
              .filter((id: string | null): id is string => !!id)
          )
        )
      );

      const productById = new Map<string, { sku: string | null; color_id: string | null }>();
      const colorById = new Map<string, string>();

      if (productIds.length > 0) {
        const { data: products } = await supabase
          .from('products')
          .select('id, sku, color_id')
          .in('id', productIds);

        (products || []).forEach((p: any) =>
          productById.set(p.id, { sku: p.sku ?? null, color_id: p.color_id ?? null })
        );

        const colorIds = Array.from(
          new Set(
            (products || [])
              .map((p: any) => p.color_id)
              .filter((id: string | null): id is string => !!id)
          )
        );

        if (colorIds.length > 0) {
          const { data: colors } = await supabase
            .from('product_colors')
            .select('id, name')
            .in('id', colorIds);

          (colors || []).forEach((c: any) => colorById.set(c.id, c.name));
        }
      }

      const formattedInvoices: Invoice[] = (data || []).map((inv: any) => ({
        id: inv.id,
        user_id: inv.admin_id,
        invoice_number: inv.invoice_number,
        invoice_date: inv.invoice_date.split('T')[0],
        due_date: inv.due_date.split('T')[0],
        customer_name: inv.customer_name,
        customer_address: inv.customer_address,
        customer_vat_number: inv.customer_vat_number,
        customer_email: inv.customer_email,
        subtotal_amount: parseFloat(inv.subtotal_amount),
        tax_amount: parseFloat(inv.tax_amount),
        total_amount: parseFloat(inv.total_amount),
        payment_method: inv.payment_method || 'cash',
        payment_status: inv.payment_status,
        payment_date: inv.payment_date,
        language: inv.language,
        currency: 'EUR',
        notes: inv.notes,
        created_at: inv.created_at,
        updated_at: inv.updated_at,
        invoice_items: (inv.invoice_items || []).map((item: any) => {
          const product = item.product_id ? productById.get(item.product_id) : undefined;
          const derivedColorName = product?.color_id ? colorById.get(product.color_id) : undefined;
          return {
            id: item.id,
            invoice_id: item.invoice_id,
            inventory_item_id: item.product_id,
            description: item.description,
            quantity: item.quantity,
            unit_price: parseFloat(item.unit_price),
            total_price: parseFloat(item.total_price),
            sku: product?.sku ?? item.sku ?? undefined,
            color_name: derivedColorName ?? item.color_name ?? undefined,
            vat_rate: 21.00,
            vat_amount: parseFloat(item.total_price) * 0.21,
            created_at: item.created_at
          };
        })
      }));

      setInvoices(formattedInvoices);
    } catch (err) {
      setError('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const createInvoice = async (invoiceData: Omit<Invoice, 'id' | 'created_at' | 'updated_at'>): Promise<Invoice | null> => {
    if (!user) return null;

    try {
      setLoading(true);

      if (user.role !== 'admin' && user.role !== 'worker') {
        throw new Error('Unauthorized to create invoices');
      }
      const adminId = getAdminId(user);
      if (!adminId) throw new Error('Unable to determine the company for this invoice');

      // Insert invoice
      const { data: invoice, error: invoiceError } = await supabase
        .from('invoices')
        .insert({
          admin_id: adminId,
          invoice_number: invoiceData.invoice_number,
          customer_name: invoiceData.customer_name,
          customer_email: invoiceData.customer_email,
          customer_address: invoiceData.customer_address,
          customer_vat_number: invoiceData.customer_vat_number,
          invoice_date: invoiceData.invoice_date,
          due_date: invoiceData.due_date,
          subtotal_amount: invoiceData.subtotal_amount,
          tax_amount: invoiceData.tax_amount,
          total_amount: invoiceData.total_amount,
          payment_status: invoiceData.payment_status,
          language: invoiceData.language,
          notes: invoiceData.notes,
          created_by: user.id
        })
        .select()
        .single();

      if (invoiceError) throw invoiceError;

      // Insert invoice items
      if (invoiceData.invoice_items && invoiceData.invoice_items.length > 0) {
        const itemsToInsert = invoiceData.invoice_items.map(item => ({
          invoice_id: invoice.id,
          product_id: item.inventory_item_id,
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price,
          total_price: item.total_price
        }));

        const { error: itemsError } = await supabase
          .from('invoice_items')
          .insert(itemsToInsert);

        if (itemsError) throw itemsError;
      }


      await supabase.from('activity_logs').insert({
        admin_id: adminId,
        user_id: user.id,
        action_type: 'invoice',
        entity_type: 'invoice',
        entity_id: invoice.id,
        description: `${user.full_name || user.email} created invoice ${invoiceData.invoice_number}`,
        metadata: {
          invoice_number: invoiceData.invoice_number,
          customer_name: invoiceData.customer_name,
          total_amount: invoiceData.total_amount
        },
        created_at: new Date().toISOString()
      });

      await loadInvoices();

      return invoice as any;
    } catch (err) {
      setError('Failed to create invoice');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const updateInvoiceStatus = async (invoiceId: string, status: Invoice['payment_status']): Promise<boolean> => {
    try {
      setLoading(true);

      const updateData: any = {
        payment_status: status,
        updated_at: new Date().toISOString()
      };

      if (status === 'paid') {
        updateData.payment_date = new Date().toISOString();
      }

      const { error } = await supabase
        .from('invoices')
        .update(updateData)
        .eq('id', invoiceId);

      if (error) throw error;

      await loadInvoices();
      return true;
    } catch (err) {
      setError('Failed to update invoice status');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const cancelInvoice = async (invoiceId: string, reason?: string): Promise<boolean> => {
    if (!user) return false;

    try {
      setLoading(true);

      const invoice = invoices.find(inv => inv.id === invoiceId);

      const { error } = await supabase
        .from('invoices')
        .update({
          payment_status: 'cancelled',
          cancellation_reason: reason || null,
          cancelled_at: new Date().toISOString(),
          cancelled_by: user.id,
          updated_at: new Date().toISOString()
        })
        .eq('id', invoiceId);

      if (error) throw error;

      const adminId = getAdminId(user);
      await supabase.from('activity_logs').insert({
        admin_id: adminId,
        user_id: user.id,
        action_type: 'invoice_cancelled',
        entity_type: 'invoice',
        entity_id: invoiceId,
        description: `${user.full_name || user.email} cancelled invoice ${invoice?.invoice_number || invoiceId}`,
        metadata: {
          invoice_number: invoice?.invoice_number,
          customer_name: invoice?.customer_name,
          total_amount: invoice?.total_amount,
          cancellation_reason: reason
        },
        created_at: new Date().toISOString()
      });

      await loadInvoices();
      return true;
    } catch (err) {
      setError('Failed to cancel invoice');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const generateSalesReport = (filter: SalesReportFilter): SalesReportData[] => {
    const startDate = new Date(filter.startDate);
    const endDate = new Date(filter.endDate);

    const filteredInvoices = invoices.filter(invoice => {
      const invoiceDate = new Date(invoice.invoice_date);
      const matchesDate = invoiceDate >= startDate && invoiceDate <= endDate;
      const matchesStatus = !filter.paymentStatus || filter.paymentStatus === 'all' || invoice.payment_status === filter.paymentStatus;
      const matchesMethod = !filter.paymentMethod || filter.paymentMethod === 'all' || invoice.payment_method === filter.paymentMethod;

      return matchesDate && matchesStatus && matchesMethod;
    });

    const reportData: Record<string, SalesReportData> = {};

    filteredInvoices.forEach(invoice => {
      const date = invoice.invoice_date;

      if (!reportData[date]) {
        reportData[date] = {
          period: date,
          totalInvoices: 0,
          totalRevenue: 0,
          totalTax: 0,
          paymentBreakdown: {
            cash: 0,
            pin: 0,
            lowan: 0,
            bankTransfer: 0
          },
          statusBreakdown: {
            paid: 0,
            unpaid: 0,
            cancelled: 0
          }
        };
      }

      const data = reportData[date];
      data.totalInvoices++;

      if (invoice.payment_status !== 'cancelled') {
        data.totalRevenue += invoice.total_amount;
        data.totalTax += invoice.tax_amount;
      }

      if (invoice.payment_status !== 'cancelled') {
        switch (invoice.payment_method) {
          case 'cash':
            data.paymentBreakdown.cash += invoice.total_amount;
            break;
          case 'pin':
            data.paymentBreakdown.pin += invoice.total_amount;
            break;
          case 'lowan':
            data.paymentBreakdown.lowan += invoice.total_amount;
            break;
          case 'bank_transfer':
            data.paymentBreakdown.bankTransfer += invoice.total_amount;
            break;
        }
      }

      switch (invoice.payment_status) {
        case 'paid':
          data.statusBreakdown.paid++;
          break;
        case 'unpaid':
        case 'overdue':
          data.statusBreakdown.unpaid++;
          break;
        case 'cancelled':
          data.statusBreakdown.cancelled++;
          break;
      }
    });

    return Object.values(reportData).sort((a, b) =>
      new Date(a.period).getTime() - new Date(b.period).getTime()
    );
  };

  const exportInvoicesCSV = (filter?: SalesReportFilter) => {
    let exportInvoices = invoices;

    if (filter) {
      const startDate = new Date(filter.startDate);
      const endDate = new Date(filter.endDate);

      exportInvoices = invoices.filter(invoice => {
        const invoiceDate = new Date(invoice.invoice_date);
        return invoiceDate >= startDate && invoiceDate <= endDate;
      });
    }

    const csvContent = [
      ['Invoice Number', 'Date', 'Customer', 'Total Amount', 'Payment Method', 'Status'].join(','),
      ...exportInvoices.map(invoice => [
        `"${invoice.invoice_number}"`,
        invoice.invoice_date,
        `"${invoice.customer_name}"`,
        invoice.total_amount.toFixed(2),
        invoice.payment_method,
        invoice.payment_status
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `invoices_export_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  return {
    invoices,
    loading,
    error,
    createInvoice,
    updateInvoiceStatus,
    cancelInvoice,
    generateSalesReport,
    exportInvoicesCSV,
    refreshInvoices: loadInvoices
  };
};
